'use memo';

import {
  Condition,
  DepositPayable,
  FormCondition,
  LinkedDateImpact,
  RenderedClauseText,
  StaffPreviewLinkedDatesRequestSourceDateType,
  useInvalidateUseStaffGetContractDates,
  useStaffPreviewLinkedDates,
} from '@urbanx/agx.campaigns.client';
import {
  AgxBadge,
  AgxDatePicker,
  AgxDivider,
  AgxSpacer,
  AgxTypography,
  formatDate,
  toDateOnlyString,
} from '@urbanx/agx.ui-components2';
import { useEffect, useRef, useState } from 'react';
import { LinkedDateWarningModal } from '../../modals/LinkedDateWarningModal';
import { useFormConditionDueDate } from '../../components/useFormConditionDueDate';
import '../ConditionDatePicker.scss';

interface SpecialConditionClauseProps {
  clause: RenderedClauseText;
  conditionId: string;
  contractId: string;
  campaignId: string;
  isLastClause: boolean;
  dueDateCondition: FormCondition | undefined;
  onDueDateUpdated: (
    conditionId: string,
    clauseId: string,
    newDueDateCondition: FormCondition | undefined,
    newDueDate: string | undefined
  ) => void | Promise<void>;
}

interface PendingDueDateChange {
  requestVersion: number;
  condition: FormCondition;
  dueDate: string | undefined;
}

const getInitialDueDateCondition = (
  dueDateCondition: FormCondition | undefined,
  clauseDueDate: string | undefined
): FormCondition => {
  if (dueDateCondition?.condition) {
    return {
      ...dueDateCondition,
      depositPayable:
        dueDateCondition.depositPayable ??
        (dueDateCondition.condition === Condition.OnSpecificDate
          ? DepositPayable.On
          : DepositPayable.NDaysFrom),
    };
  }

  if (dueDateCondition?.referenceDate) {
    return {
      ...dueDateCondition,
      condition: Condition.OnSpecificDate,
      depositPayable: DepositPayable.On,
    };
  }

  if (clauseDueDate) {
    return {
      condition: Condition.OnSpecificDate,
      depositPayable: DepositPayable.On,
      referenceDate: clauseDueDate,
    };
  }

  return {};
};

const getDueDateBadgeText = (dueDate: Date | null) => {
  if (!dueDate) return undefined;

  return formatDate(dueDate.toISOString());
};

const SpecialConditionClause = ({
  clause,
  conditionId,
  contractId,
  campaignId,
  isLastClause,
  dueDateCondition,
  onDueDateUpdated,
}: SpecialConditionClauseProps) => {
  const [linkedDateImpacts, setLinkedDateImpacts] = useState<LinkedDateImpact[]>([]);
  const [pendingDueDateChange, setPendingDueDateChange] = useState<PendingDueDateChange>();
  const dueDateRequestVersion = useRef(0);

  const [localDueDateCondition, setLocalDueDateCondition] = useState<FormCondition>(() =>
    getInitialDueDateCondition(dueDateCondition, clause.dueDate)
  );
  const calculatedDueDate = useFormConditionDueDate(localDueDateCondition);
  const dueDateBadgeText = getDueDateBadgeText(calculatedDueDate.dueDate);

  useEffect(() => {
    dueDateRequestVersion.current += 1;
    setLinkedDateImpacts([]);
    setPendingDueDateChange(undefined);

    setLocalDueDateCondition(getInitialDueDateCondition(dueDateCondition, clause.dueDate));
  }, [dueDateCondition, clause.dueDate]);

  const invalidateUseStaffGetContractDates = useInvalidateUseStaffGetContractDates();

  const { staffPreviewLinkedDates } = useStaffPreviewLinkedDates({
    onSuccess: () => {},
    onError: () => {},
  });

  const saveDueDate = async (
    newDueDateCondition: FormCondition,
    newDueDate: string | undefined
  ) => {
    await onDueDateUpdated(
      conditionId,
      clause.clauseId ?? '',
      newDueDateCondition.condition ? newDueDateCondition : undefined,
      newDueDate
    );
    await invalidateUseStaffGetContractDates(campaignId, contractId);
  };

  const onDueDateChanged = async (value: Date | null) => {
    if (!clause?.clauseId) return;

    dueDateRequestVersion.current += 1;
    const requestVersion = dueDateRequestVersion.current;

    const dueDate = toDateOnlyString(value);
    const newDueDateCondition = dueDate
      ? {
          ...localDueDateCondition,
          condition: Condition.OnSpecificDate,
          referenceDate: dueDate,
          daysFromDate: undefined,
          conditionType: undefined,
          specialConditionId: undefined,
        }
      : {};

    setLocalDueDateCondition(newDueDateCondition);
    setLinkedDateImpacts([]);
    setPendingDueDateChange(undefined);

    try {
      const preview = await staffPreviewLinkedDates({
        campaignId,
        contractId,
        body: {
          sourceDateType: StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate,
          proposedDate: dueDate,
          specialConditionClauseId: clause.clauseId,
        },
      });

      if (requestVersion !== dueDateRequestVersion.current) return;

      const impactedDates = preview?.impactedDates ?? [];
      if (impactedDates.length > 0) {
        setLinkedDateImpacts(impactedDates);
        setPendingDueDateChange({
          requestVersion,
          condition: newDueDateCondition,
          dueDate,
        });
        return;
      }

      await saveDueDate(newDueDateCondition, dueDate);
    } catch {
      if (requestVersion !== dueDateRequestVersion.current) return;

      setLocalDueDateCondition(getInitialDueDateCondition(dueDateCondition, clause.dueDate));
    }
  };

  const onCancelLinkedDateWarning = () => {
    dueDateRequestVersion.current += 1;
    setLinkedDateImpacts([]);
    setPendingDueDateChange(undefined);
    setLocalDueDateCondition(getInitialDueDateCondition(dueDateCondition, clause.dueDate));
  };

  const onConfirmLinkedDateWarning = () => {
    if (
      !pendingDueDateChange ||
      pendingDueDateChange.requestVersion !== dueDateRequestVersion.current
    )
      return;

    setLinkedDateImpacts([]);
    setPendingDueDateChange(undefined);
    void saveDueDate(pendingDueDateChange.condition, pendingDueDateChange.dueDate);
  };

  return (
    <>
      <AgxSpacer
        className="special-condition-clause-content"
        fill
        direction="vertical"
        gap="spacing-xl"
      >
        <AgxTypography
          type="body_ui"
          size="small"
          id={`clause-${clause.clauseId}`}
          color="var(--text-secondary)"
        >
          Clause {clause.clausePosition}: {clause.clauseText}
        </AgxTypography>
        {dueDateBadgeText && (
          <AgxSpacer direction="horizontal" gap="spacing-sm" contentAlignment="center">
            <AgxTypography
              id={`clause-${clause.clauseId}-due-on`}
              type="body_ui"
              size="small"
              color="var(--text-primary)"
            >
              Due on
            </AgxTypography>
            <AgxBadge type="colourfill" colour="grey" size="md" text={dueDateBadgeText} />
          </AgxSpacer>
        )}
        <AgxDatePicker
          id={`clause-${clause.clauseId}-date`}
          className="contract-information-date-picker"
          label="Due Date"
          optionalText=""
          value={calculatedDueDate.dueDate}
          onChange={onDueDateChanged}
        />
      </AgxSpacer>
      {!isLastClause && <AgxDivider variant={'horizontal'} />}
      {linkedDateImpacts.length > 0 && (
        <LinkedDateWarningModal
          impactedDates={linkedDateImpacts}
          sourceDateTypes={[StaffPreviewLinkedDatesRequestSourceDateType.SpecialConditionDate]}
          onCancel={onCancelLinkedDateWarning}
          onConfirm={onConfirmLinkedDateWarning}
        />
      )}
    </>
  );
};

export default SpecialConditionClause;
